import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Settings as SettingsIcon,
  User,
  Key,
  Shield,
  Database,
  Cloud,
  Save,
  Eye,
  EyeOff,
  Check,
  X,
  AlertTriangle,
  Info,
  ExternalLink,
  Trash2,
  RefreshCw,
  Building2
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiService } from '@/services/api'
import { logger } from '@/utils/logger'
import OrganizationSettings from '@/components/settings/OrganizationSettings'

interface ServiceCredentials {
  serviceName: string
  displayName: string
  icon: React.ComponentType<any>
  description: string
  fields: {
    key: string
    label: string
    type: 'text' | 'password' | 'url'
    placeholder: string
    required: boolean
  }[]
  connected: boolean
  hasCredentials: boolean
}

const serviceConfigs: ServiceCredentials[] = [
  {
    serviceName: 'google-drive',
    displayName: 'Google Drive',
    icon: Cloud,
    description: 'Connect to Google Drive to import files from your account',
    fields: [
      {
        key: 'clientId',
        label: 'Client ID',
        type: 'text',
        placeholder: 'your-client-id.googleusercontent.com',
        required: true
      },
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        placeholder: 'Your Google API Key',
        required: true
      }
    ],
    connected: false,
    hasCredentials: false
  },
  {
    serviceName: 'sharepoint',
    displayName: 'Microsoft SharePoint',
    icon: Database,
    description: 'Connect to SharePoint to access document libraries',
    fields: [
      {
        key: 'clientId',
        label: 'Application (Client) ID',
        type: 'text',
        placeholder: 'your-app-client-id',
        required: true
      },
      {
        key: 'tenantId',
        label: 'Directory (Tenant) ID',
        type: 'text',
        placeholder: 'your-tenant-id',
        required: true
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        type: 'password',
        placeholder: 'Your client secret',
        required: true
      }
    ],
    connected: false,
    hasCredentials: false
  },
  {
    serviceName: 'aws-s3',
    displayName: 'Amazon S3',
    icon: Cloud,
    description: 'Connect to AWS S3 buckets for file storage',
    fields: [
      {
        key: 'accessKeyId',
        label: 'Access Key ID',
        type: 'text',
        placeholder: 'AKIA...',
        required: true
      },
      {
        key: 'secretAccessKey',
        label: 'Secret Access Key',
        type: 'password',
        placeholder: 'Your secret access key',
        required: true
      },
      {
        key: 'region',
        label: 'AWS Region',
        type: 'text',
        placeholder: 'us-east-1',
        required: true
      }
    ],
    connected: false,
    hasCredentials: false
  }
]

export default function Settings() {
  const [activeTab, setActiveTab] = useState<'credentials' | 'organization'>('credentials')
  const [services, setServices] = useState<ServiceCredentials[]>(serviceConfigs)
  const [editingService, setEditingService] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<Record<string, any>>({})
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({})
  const [saveStatus, setSaveStatus] = useState<Record<string, 'idle' | 'saving' | 'success' | 'error'>>({})

  useEffect(() => {
    loadCredentialStatus()
  }, [])

  const loadCredentialStatus = async () => {
    try {
      // Check Google Drive OAuth status
      const driveStatus = await apiService.checkGoogleDriveConnection()
      
      // Check other credentials
      const response = await apiService.getCredentialStatus()
      
      setServices(prev => prev.map(service => {
        if (service.serviceName === 'google-drive') {
          return {
            ...service,
            connected: driveStatus?.connected || false,
            hasCredentials: driveStatus?.connected || false
          }
        }
        return {
          ...service,
          connected: response.success ? (response.data.status[service.serviceName]?.connected || false) : false,
          hasCredentials: response.success ? (response.data.status[service.serviceName]?.hasCredentials || false) : false
        }
      }))
    } catch (error) {
      logger.error('Failed to load credential status', undefined, error as Error)
    }
  }

  const handleConnectGoogleDrive = async () => {
    try {
      const response = await apiService.connectGoogleDrive()
      if (response.success && response.data.authUrl) {
        window.location.href = response.data.authUrl
      }
    } catch (error) {
      logger.error('Failed to connect Google Drive', undefined, error as Error)
    }
  }

  const handleEditService = async (serviceName: string) => {
    // For Google Drive, start OAuth flow instead of showing form
    if (serviceName === 'google-drive') {
      handleConnectGoogleDrive()
      return
    }
    
    setEditingService(serviceName)
    setLoading(prev => ({ ...prev, [serviceName]: true }))
    
    try {
      // Try to load existing credentials
      const response = await apiService.getServiceCredentials(serviceName, 'oauth')
      if (response.success) {
        setCredentials(prev => ({
          ...prev,
          [serviceName]: response.data.credentials
        }))
      }
    } catch (error) {
      // No existing credentials, start with empty form
      const service = services.find(s => s.serviceName === serviceName)
      if (service) {
        const emptyCredentials: Record<string, string> = {}
        service.fields.forEach(field => {
          emptyCredentials[field.key] = ''
        })
        setCredentials(prev => ({
          ...prev,
          [serviceName]: emptyCredentials
        }))
      }
    } finally {
      setLoading(prev => ({ ...prev, [serviceName]: false }))
    }
  }

  const handleSaveCredentials = async (serviceName: string) => {
    setSaveStatus(prev => ({ ...prev, [serviceName]: 'saving' }))
    
    try {
      const serviceCredentials = credentials[serviceName]
      if (!serviceCredentials) {
        throw new Error('No credentials to save')
      }

      await apiService.saveServiceCredentials(serviceName, 'oauth', serviceCredentials)
      
      setSaveStatus(prev => ({ ...prev, [serviceName]: 'success' }))
      setEditingService(null)
      
      // Refresh status
      await loadCredentialStatus()
      
      // Clear success status after 3 seconds
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [serviceName]: 'idle' }))
      }, 3000)
      
      logger.info('Credentials saved successfully', { serviceName })
    } catch (error) {
      setSaveStatus(prev => ({ ...prev, [serviceName]: 'error' }))
      logger.error('Failed to save credentials', { serviceName }, error as Error)
      
      // Clear error status after 5 seconds
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [serviceName]: 'idle' }))
      }, 5000)
    }
  }

  const handleDeleteCredentials = async (serviceName: string) => {
    if (!confirm(`Are you sure you want to delete ${serviceName} credentials?`)) {
      return
    }

    setLoading(prev => ({ ...prev, [serviceName]: true }))
    
    try {
      await apiService.deleteServiceCredentials(serviceName, 'oauth')
      await loadCredentialStatus()
      setEditingService(null)
      setCredentials(prev => {
        const newCreds = { ...prev }
        delete newCreds[serviceName]
        return newCreds
      })
      
      logger.info('Credentials deleted successfully', { serviceName })
    } catch (error) {
      logger.error('Failed to delete credentials', { serviceName }, error as Error)
    } finally {
      setLoading(prev => ({ ...prev, [serviceName]: false }))
    }
  }

  const updateCredentialField = (serviceName: string, fieldKey: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName],
        [fieldKey]: value
      }
    }))
  }

  const togglePasswordVisibility = (serviceName: string, fieldKey: string) => {
    const key = `${serviceName}-${fieldKey}`
    setShowPasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const getStatusBadge = (service: ServiceCredentials) => {
    if (service.connected) {
      return <Badge className="bg-green-100 text-green-800">Connected</Badge>
    }
    if (service.hasCredentials) {
      return <Badge className="bg-yellow-100 text-yellow-800">Configured</Badge>
    }
    return <Badge variant="outline">Not Configured</Badge>
  }

  const getSaveButtonContent = (serviceName: string) => {
    const status = saveStatus[serviceName] || 'idle'
    switch (status) {
      case 'saving':
        return (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Saving...
          </>
        )
      case 'success':
        return (
          <>
            <Check className="w-4 h-4 mr-2" />
            Saved!
          </>
        )
      case 'error':
        return (
          <>
            <X className="w-4 h-4 mr-2" />
            Error
          </>
        )
      default:
        return (
          <>
            <Save className="w-4 h-4 mr-2" />
            Save Credentials
          </>
        )
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-6">
          <div className="flex items-center space-x-3">
            <SettingsIcon className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Settings</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your account settings and API integrations
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex space-x-8">
            {[
              { id: 'credentials' as const, label: 'API Credentials', icon: Key },
              { id: 'organization' as const, label: 'Organization', icon: Building2 }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-1 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'organization' && <OrganizationSettings />}

        {activeTab === 'credentials' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">API Credentials</h2>
              <p className="text-muted-foreground">
                Configure your API credentials to connect external services like Google Drive, SharePoint, and AWS S3.
              </p>
            </div>

            <div className="space-y-6">
              {services.map((service) => (
                <Card key={service.serviceName} className="relative">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <service.icon className="w-6 h-6 text-primary" />
                        <div>
                          <CardTitle className="text-lg">{service.displayName}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {service.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getStatusBadge(service)}
                        {editingService === service.serviceName ? (
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingService(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSaveCredentials(service.serviceName)}
                              disabled={saveStatus[service.serviceName] === 'saving'}
                              className={
                                saveStatus[service.serviceName] === 'success' ? 'bg-green-600 hover:bg-green-700' :
                                saveStatus[service.serviceName] === 'error' ? 'bg-red-600 hover:bg-red-700' : ''
                              }
                            >
                              {getSaveButtonContent(service.serviceName)}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            {service.hasCredentials && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCredentials(service.serviceName)}
                                disabled={loading[service.serviceName]}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleEditService(service.serviceName)}
                              disabled={loading[service.serviceName]}
                            >
                              {loading[service.serviceName] ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Key className="w-4 h-4 mr-2" />
                              )}
                              {service.hasCredentials ? 'Edit' : 'Configure'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {editingService === service.serviceName && (
                    <CardContent>
                      <div className="space-y-4">
                        {service.fields.map((field) => (
                          <div key={field.key}>
                            <label className="block text-sm font-medium mb-2">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <div className="relative">
                              <Input
                                type={
                                  field.type === 'password' && !showPasswords[`${service.serviceName}-${field.key}`]
                                    ? 'password'
                                    : 'text'
                                }
                                placeholder={field.placeholder}
                                value={credentials[service.serviceName]?.[field.key] || ''}
                                onChange={(e) => updateCredentialField(service.serviceName, field.key, e.target.value)}
                                className={field.type === 'password' ? 'pr-10' : ''}
                              />
                              {field.type === 'password' && (
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility(service.serviceName, field.key)}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                  {showPasswords[`${service.serviceName}-${field.key}`] ? (
                                    <EyeOff className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        {service.serviceName === 'google-drive' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                              <div className="text-sm">
                                <p className="font-medium text-blue-900 mb-1">How to get Google Drive credentials:</p>
                                <ol className="list-decimal list-inside space-y-1 text-blue-800">
                                  <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                                  <li>Create a project or select existing one</li>
                                  <li>Enable Google Drive API</li>
                                  <li>Create OAuth 2.0 credentials</li>
                                  <li>Add http://localhost:3000 to authorized origins</li>
                                </ol>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Profile Settings</h2>
              <p className="text-muted-foreground">
                Manage your account information and preferences.
              </p>
            </div>
            
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Profile management coming soon...</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'preferences' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Preferences</h2>
              <p className="text-muted-foreground">
                Customize your DataRaiils experience.
              </p>
            </div>
            
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Preferences panel coming soon...</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  )
}
